import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useSearchParams } from 'react-router-dom';

interface BuildNumberProps {
    buildNumber: number;
    lastUpdated: string;
}

// Release notes object - update this with each release
const releaseNotes = {
    // Add more release notes as needed
    233: {
        date: '2025-07-09',
        notes: [
            'Added league name, day date, and duration to scorecard',
            'Fixed theming issues with scorecard',
            'Summary scores now shown on scorecard below buttons',
            'Fixed summary score not displaying properly on hole-entry page in dark mode'
        ]
    },
    230: {
        date: '2025-07-09',
        notes: [
            'Fixed screenshot feedback not working (CORS issue)',
            'Dark mode theme now works better in most cases'
        ]
    },
    218: {
        date: '2025-07-06',
        notes: [
            'Added CSV export by tees',
            'Added CSV export by flights',
            'Added more descriptive payout breakdown to CSV export by member',
            'Added more totals columns to CSV export by tee and by flight'
        ]
    },
    217: {
        date: '2025-07-05',
        notes: [
            'Added league reports page for admins to generate reports for league payouts',
            'Small data migration'
        ]
    },
    215: {
        date: '2025-07-03',
        notes: [
            'Added handicap details modal to league page',
            'Handicap details modal now explains why handicaps are calculated the way they are',
            'Pin prizes on Results page now sorted by hole then tee name'
        ]
    },
    209: {
        date: '2025-07-02',
        notes: [
            'Fixed issue in Admin mode where league results were not being saved when handicap was recalculated',
            'Handicaps now propagate from league results to user dashboards at activation of a week of play to sidestep several edge cases',
            'Admin can now assign pin prize winners prior to locking the week',
            'At week completion time in Admin mode, pin prizes are re-validated to ensure winners are still eligible'
        ]
    },
    207: {
        date: '2025-07-01',
        notes: [
            'Fixed issue with handicap recalculation at completion of a week of play',
            'Fixed issue with handicap propagation from league results to user dashboards',
            'Fixed issue with flight propagation from league results to user dashboards',
            'Fixed issue with handicap calculation steps propagation from league results to user dashboards',
        ]
    },
    203: {
        date: '2025-06-26',
        notes: [
            'Added navigation buttons to League Results page',
            'Handicaps now being recalculated at Completion of a week of play',
            'Fixed issue with skins payout display on league results page',
        ]
    },
    163: {
        date: '2025-06-22',
        notes: [
            'Fixed issue in mass import script for legacy data from spreadsheet',
            'Normalized round status verbiage',
            'Normalized button colors',
            'Most buttons now use text instead of icons'
        ]
    },
    158: {
        date: '2025-06-22',
        notes: [
            'League results page now shows useful data',
            'Fixed various UI scrolling issues for smaller screens',
            'Fixed re-calculation of league results in admin mode',
            'Added payout details to league results page'
        ]
    },
    141: {
        date: '2025-06-14',
        notes: [
            'Fixed side scrolling issues on dashboard and profile pages',
            'Fixed display width issues on some devices which caused items to overflow offscreen to the right',
            'Toggle buttons now use SegmentedControl component',
            'SegmentedControl component now has spinner animation when loading',
            'All user-reported issues have now been fixed'
        ]
    },
    135: {
        date: '2025-06-13',
        notes: [
            'Added a new "Report Issue" button to the navbar',
            'Enlarged modal button text',
            'Added toast notifications for various actions'
        ]
    },
    134: {
        date: '2025-06-12',
        notes: [
            'Reset password now works properly in all environments',
            'Password reset now redirects to login page with email pre-filled',
        ]
    },
    128: {
        date: '2025-06-10',
        notes: [
            'No more "double scroll" issue on dashboard page',
            'Enhanced scorecard UI',
            'Added dots above hole numbers to indicate strokes given',
        ]
    },
    127: {
        date: '2025-06-09',
        notes: [
            'Completely refactored data access into repos / services',
            'Added dependency injection for testing purposes',
            'UI code now uses services exclusively; no more direct firestore access',
            'Added a new "Fast Score" entry mode for entering scores quickly after your round has been played',
            'Modals now use a more consistent design',

        ]
    },
    118: {
        date: '2025-06-02',
        notes: [
            'Button response times improved',
            'Score now saves to cloud transparently in background between holes',
            'Local and cloud state sync issues resolved'
        ]
    },
    117: {
        date: '2025-06-01',
        notes: [
            'Added some basic tutorials for horizontal scrolling',
            'Added release notes capability',
            'Enhanced dashboard league round info, including duration',
            'Added stats totals to scorecard',
        ]
    },
    116: {
        date: '2025-05-31',
        notes: [
            'Back button on scorecard now works properly',
            'Page titles updated across entire app',
            'Alerts are now modal dialogs',
        ]
    },
    113: {
        date: '2025-05-30',
        notes: [
            'Gray/White now renders proper tee colors on each individual hole',
            'Round completion no longer prompts for stats if stats tracking is disabled'
        ]
    },
    112: {
        date: '2025-05-29',
        notes: [
            'Now tracking release notes for each build',
            'Release notes are now displayed in a modal dialog on profile page'
        ]
    },
};

const BuildNumber: React.FC<BuildNumberProps> = ({ buildNumber, lastUpdated }) => {
    const { theme } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showDialog, setShowDialog] = useState(false);

    // Check URL for release notes parameter on mount
    useEffect(() => {
        if (searchParams.get('showReleaseNotes') === 'true') {
            setShowDialog(true);
        }
    }, [searchParams]);

    const handleShowDialog = () => {
        setShowDialog(true);
        // Update URL with query parameter
        setSearchParams({ showReleaseNotes: 'true' });
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
        // Remove query parameter from URL
        setSearchParams({});
    };

    const style = {
        position: 'fixed' as const,
        bottom: theme.spacing.MEDIUM,
        right: theme.spacing.MEDIUM,
        fontSize: theme.fontSize.MEDIUM,
        color: theme.colors.TEXT_SECONDARY,
        opacity: 0.7,
        fontFamily: 'monospace',
        padding: `${theme.spacing.SMALL}px ${theme.spacing.MEDIUM}px`,
        backgroundColor: theme.colors.SURFACE,
        borderRadius: theme.borderRadius.SMALL,
        border: `1px solid ${theme.colors.BORDER}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.SMALL,
        '&:hover': {
            opacity: 1,
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.15)',
            backgroundColor: theme.colors.BACKGROUND
        },
        '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
    };

    const dialogStyle = {
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.colors.SURFACE,
        padding: theme.spacing.LARGE,
        borderRadius: theme.borderRadius.MEDIUM,
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        zIndex: 1000,
        maxWidth: '90%',
        width: '400px',
        maxHeight: '80vh',
        overflowY: 'auto' as const,
    };

    const overlayStyle = {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 999,
    };

    const closeButtonStyle = {
        position: 'absolute' as const,
        top: theme.spacing.MEDIUM,
        right: theme.spacing.MEDIUM,
        background: 'none',
        border: 'none',
        color: theme.colors.TEXT_SECONDARY,
        cursor: 'pointer',
        fontSize: '1.5em',
        padding: '4px',
        lineHeight: 1,
    };

    const releaseNoteStyle = {
        marginBottom: theme.spacing.MEDIUM,
        padding: theme.spacing.MEDIUM,
        backgroundColor: theme.colors.BACKGROUND,
        borderRadius: theme.borderRadius.SMALL,
        border: `1px solid ${theme.colors.BORDER}`,
    };

    //    const currentRelease = releaseNotes[buildNumber as keyof typeof releaseNotes];

    return (
        <>
            <div
                style={style}
                onClick={handleShowDialog}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.15)';
                    e.currentTarget.style.backgroundColor = theme.colors.BACKGROUND;
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.7';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    e.currentTarget.style.backgroundColor = theme.colors.SURFACE;
                }}
            >
                <span>ℹ️</span>
                Build #{buildNumber} • {new Date(lastUpdated).toLocaleDateString()}
            </div>

            {showDialog && (
                <>
                    <div style={overlayStyle} onClick={handleCloseDialog} />
                    <div style={dialogStyle} onClick={handleCloseDialog}>
                        <button
                            style={closeButtonStyle}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCloseDialog();
                            }}
                        >
                            ×
                        </button>
                        <h2 style={{
                            color: theme.colors.TEXT,
                            marginBottom: theme.spacing.MEDIUM,
                            fontSize: '1.2em'
                        }}>
                            Release Notes
                        </h2>
                        {Object.entries(releaseNotes)
                            .sort(([a], [b]) => Number(b) - Number(a)) // Sort by version number in descending order
                            .map(([version, release]) => (
                                <div key={version} style={releaseNoteStyle}>
                                    <div style={{
                                        color: theme.colors.TEXT_SECONDARY,
                                        marginBottom: theme.spacing.SMALL,
                                        fontSize: '0.9em'
                                    }}>
                                        Version {version} • {new Date(release.date + 'T12:00:00').toLocaleDateString()}
                                    </div>
                                    <ul style={{
                                        margin: 0,
                                        paddingLeft: theme.spacing.MEDIUM,
                                        color: theme.colors.TEXT
                                    }}>
                                        {release.notes.map((note, index) => (
                                            <li key={index} style={{ marginBottom: theme.spacing.SMALL }}>
                                                {note}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                    </div>
                </>
            )}
        </>
    );
};

export default BuildNumber; 